import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { BIN_IMG, formatXCD } from "@/lib/constants";
import { Trash2 } from "lucide-react";

export default function BinPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ address: "", rental_date: "", duration_days: 7, notes: "" });
  const [price, setPrice] = useState(0);
  const [s, setS] = useState(false);
  useLiveData(() => { api.get("/pricing").then(r => setPrice(r.data.bin || 0)).catch(() => {}); });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to book"); return nav("/login"); }
    setS(true);
    try {
      const { data } = await api.post("/bookings/bin", form);
      toast.success(`Bin rental booked — ${data.invoice_number}`);
      nav("/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setS(false); }
  };

  return (
    <Layout>
      <section className="relative">
        <div className="absolute inset-0"><img src={BIN_IMG} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-slate-900/70" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Service · Bin Rentals</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-2">Construction & cleanup bins.</h1>
          <p className="mt-3 max-w-xl text-white/90">Flat EC$350 per bin — delivered, used, and collected.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-slate-200 rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#1D4ED8]"><Trash2 className="h-5 w-5" strokeWidth={2.5} /><span className="tag-overline">Book now</span></div>
          <h2 className="font-heading font-black text-2xl mt-1">Bin rental booking</h2>
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label className="text-sm font-bold text-slate-900">Delivery address</Label>
              <Input data-testid="bin-address" required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Rental date</Label>
                <Input data-testid="bin-date" type="date" required value={form.rental_date} onChange={e => setForm({ ...form, rental_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Duration (days)</Label>
                <Input data-testid="bin-duration" type="number" min={1} required value={form.duration_days}
                       onChange={e => setForm({ ...form, duration_days: parseInt(e.target.value || 1, 10) })} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-900">Notes</Label>
              <Textarea data-testid="bin-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Total</div>
                <div className="font-heading font-black text-3xl text-slate-900" data-testid="bin-total">{formatXCD(price)}</div>
              </div>
              <Button type="submit" disabled={s} data-testid="bin-submit-btn"
                      className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold h-12 px-6">
                {s ? "Booking…" : "Confirm booking"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
